// Global Types
import type {
  $Children,
} from 'common.types';
import type {
  $Translate,
} from 'common.language';

// Global Components
import * as React from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from 'reactstrap';

// Helpers
import moment from 'moment';

// Types
type $Props = $Translate & {
  children: $Children;
  id: string;
  setStart: (value: moment.Moment | null) => unknown;
  start: moment.Moment | null;
};

type $State = {
  time: moment.Moment | null;
  visible: boolean;
};

class ClockDialogView extends React.Component<$Props, $State> {
  counter: null | ReturnType<typeof setTimeout>;

  constructor(props: $Props) {
    super(props);

    this.state = {
      time: null,
      visible: false,
    };

    this.counter = null;
  }

  componentDidMount(): void {
    this.updateTime();
  }

  componentWillUnmount(): void {
    if (this.counter !== null) {
      clearTimeout(this.counter);
    }
  }

  updateTime(): void {
    const {
      setStart,
      start,
    } = this.props;

    this.counter = setTimeout(
      () => this.updateTime(),
      1000,
    );

    const time: moment.Moment = moment();

    this.setState(
      {
        time,
      },
      () => {
        if (start !== null && start.isSame(
          time,
          'seconds',
        )) {
          const newStart: moment.Moment = time.add(
            5,
            'minutes',
          );

          setStart(newStart);
        }
      },
    );
  }

  toggle(): void {
    this.setState((prevState: $State) => ({
      visible: !prevState.visible,
    }));
  }

  childrenWithToggleHandler() {
    const {
      children,
    } = this.props;

    if (React.isValidElement(children)) {
      return React.cloneElement(
        children,
        {
          // @ts-expect-error dialog
          onClick: () => this.toggle(),
        },
      );
    }

    return children;
  }

  // eslint-disable-next-line complexity
  renderStartWatchTime(minutesBefore: number) {
    const {
      start,
    } = this.props;
    const {
      time,
    } = this.state;

    if (time !== null && start !== null) {
      const now: moment.Moment = moment();
      const point: moment.Moment = moment(start).subtract(
        minutesBefore,
        'minutes',
      );
      const diff: number = point.diff(time);
      const durationMoment: moment.Moment = moment(diff);
      const invertDiff: number = time.diff(point);
      const invertDurationMoment: moment.Moment = moment(invertDiff);
      const secondsLeft: number = Math.round(diff / 1000);

      if (point.isBefore(now)) {
        return (
          <span className={'h3 text-muted'}>
            {invertDurationMoment.format('mm:ss')}
          </span>
        );
      }

      return (
        <span className={`text-bold h3 ${secondsLeft < 10 ? 'text-warning' : ''} ${secondsLeft < 3 ? 'text-bold text-danger' : ''}`}>
          {`-${durationMoment.add(
            1,
            'second',
          ).format('mm:ss')}`}
        </span>
      );
    }

    return <span className={'text-muted'}>{'---'}</span>;
  }

  renderStartWatch() {
    const {
      setStart,
      start,
      t,
    } = this.props;
    const {
      time,
    } = this.state;

    let closestStart: moment.Moment | null = null;

    if (time !== null) {
      const aproximator: number = (5 - time.minute()) % 5;

      closestStart = moment(time).add(
        aproximator,
        'minutes',
      );
    }

    return (
      <>
        <div className={'text-center h3'}>
          {start !== null ? (
            <span>
              {`${t('Start time')}: `}
              <span className={'text-bold'}>
                {start.format('HH:mm:ss')}
              </span>
            </span>
          ) : (
            <span className={'text-muted'}>
              {t('Start procedure watch')}
            </span>
          )}
        </div>
        <Table>
          <tbody>
            <tr>
              <td>{t('Class flag')}</td>
              <td className={'text-center'}>{'UP'}</td>
              <td className={'text-right py-1'}>
                {this.renderStartWatchTime(5)}
              </td>
            </tr>
            <tr>
              <td>{t('Prep. flag')}</td>
              <td className={'text-center'}>{'UP'}</td>
              <td className={'text-right py-1'}>
                {this.renderStartWatchTime(4)}
              </td>
            </tr>
            <tr>
              <td>{t('Prep. flag')}</td>
              <td className={'text-center'}>{'DOWN'}</td>
              <td className={'text-right py-1'}>
                {this.renderStartWatchTime(1)}
              </td>
            </tr>
            <tr>
              <td>{t('Class flag')}</td>
              <td className={'text-center'}>{'DOWN'}</td>
              <td className={'text-right py-1'}>
                {this.renderStartWatchTime(0)}
              </td>
            </tr>
          </tbody>
        </Table>
        <div className={'text-center'}>
          {start === null ? (
            <>
              {closestStart !== null && (
                <Button
                  className={'mr-1'}
                  color={'warning'}
                  onClick={() => closestStart !== null && setStart(closestStart.add(
                    5,
                    'minutes',
                  ).seconds(0))}
                  size={'sm'}
                >
                  {`${t('Start procedure in')} ${closestStart.format('HH:mm:00')}`}
                </Button>
              )}
              <Button
                color={'success'}
                onClick={() => time !== null && setStart(time.add(
                  5,
                  'minutes',
                ))}
                size={'sm'}
              >
                {t('Start now')}
              </Button>
            </>
          ) : (
            <Button
              color={'danger'}
              onClick={() => setStart(null)}
              size={'sm'}
            >
              {t('Stop')}
            </Button>
          )}
        </div>
      </>
    );
  }

  render() {
    const {
      id,
      t,
    } = this.props;
    const {
      time,
      visible,
    } = this.state;

    return (
      <>
        {this.childrenWithToggleHandler()}
        <Modal
          backdrop={'static'}
          centered
          id={id}
          isOpen={visible}
          size={'sm'}
          toggle={() => this.toggle()}
        >
          <ModalHeader>
            <p className={'mb-0'}>{t('Regatta clock')}</p>
          </ModalHeader>
          <ModalBody>
            <div className={'text-center'}>
              {time !== null && (
                <h1 className={'display-3'}>
                  {time.format('HH:mm:ss')}
                </h1>
              )}
            </div>
            {this.renderStartWatch()}
          </ModalBody>
          <ModalFooter>
            <Button
              color={'secondary'}
              onClick={() => this.toggle()}
            >
              {t('Close')}
            </Button>
          </ModalFooter>
        </Modal>
      </>
    );
  }
}

export default ClockDialogView;
